import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import SearchWidget from "@/components/SearchWidget";
import { Check, MapPin } from "lucide-react";

// SEO-friendly airport landing pages at /airport-parking/[slug]
// Slug is city name or IATA code lowercased (e.g. heathrow, gatwick, LHR).
const SLUG_TO_IATA = {
  heathrow: "LHR", "london-heathrow": "LHR",
  gatwick: "LGW", "london-gatwick": "LGW",
  manchester: "MAN",
  birmingham: "BHX",
  stansted: "STN", "london-stansted": "STN",
  luton: "LTN", "london-luton": "LTN",
  bristol: "BRS",
  edinburgh: "EDI",
  glasgow: "GLA",
  cardiff: "CWL",
  liverpool: "LPL",
  newcastle: "NCL",
  "east-midlands": "EMA",
  "leeds-bradford": "LBA",
  southampton: "SOU",
  bournemouth: "BOH",
  "belfast-international": "BFS",
  "belfast-city": "BHD",
};

const COPY = {
  LHR: {
    intro: "Compare official Heathrow parking, Park & Ride, Meet & Greet and approved local driveways across all five Heathrow terminals — from budget long-stay to on-terminal short-stay.",
    tips: [
      "For early flights, book Meet & Greet at Terminal 5 to save time.",
      "Park & Ride at T2/T3 typically saves 40% vs official on-terminal parking.",
      "Approved community driveways near Hounslow are the cheapest way to park at Heathrow.",
    ],
  },
  LGW: {
    intro: "Gatwick parking made simple: compare Long Stay, Short Stay, Meet & Greet at North and South terminals plus approved private parking in Crawley and Horley.",
    tips: [
      "North and South terminals have separate car parks — check your flight before booking.",
      "Meet & Greet saves the shuttle wait after a long-haul flight.",
      "Community parking in Crawley is 5–10 minutes from the terminal by taxi.",
    ],
  },
  MAN: {
    intro: "Manchester Airport parking across T1, T2 and T3. Compare Jetparks, Meet & Greet and gated community parking bays close to the terminal.",
    tips: ["Book at least 7 days ahead for the best Jetparks rates.", "Meet & Greet is the fastest option for early departures."],
  },
};

export default function AirportLanding() {
  const { slug } = useParams();
  const iata = SLUG_TO_IATA[(slug || "").toLowerCase()] || (slug || "").toUpperCase();
  const [airport, setAirport] = useState(null);
  const [offers, setOffers] = useState([]);

  useEffect(() => {
    document.title = `${(airport?.name) || iata} Parking | Compare Prices | Parkpot`;
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); return m;
    })();
    meta.content = `Compare ${(airport?.name) || iata} airport parking, Park & Ride, Meet & Greet and approved community parking spaces with Parkpot.`;
  }, [airport, iata]);

  useEffect(() => {
    api.get(`/airports/${iata}`).then(r => setAirport(r.data)).catch(() => {});
    // Sample offers for teaser (default 7-day search 30 days out)
    const d1 = new Date(); d1.setDate(d1.getDate() + 30); d1.setHours(8, 0, 0, 0);
    const d2 = new Date(d1); d2.setDate(d2.getDate() + 7); d2.setHours(22, 0, 0, 0);
    api.post("/search", { airport_code: iata, dropoff: d1.toISOString(), pickup: d2.toISOString() })
      .then(r => setOffers((r.data.offers || []).slice(0, 4))).catch(() => {});
  }, [iata]);

  const copy = COPY[iata] || { intro: `Compare airport parking at ${(airport?.name) || iata}. Official parking, Park & Ride, Meet & Greet and approved community parking — all in one search.`, tips: [] };

  if (!iata) return <div className="p-10">Unknown airport</div>;

  return (
    <div>
      <section className="bg-[var(--fpn-navy)] text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <nav className="text-xs text-slate-300"><Link to="/" className="hover:text-white">Home</Link> · <Link to="/airport-parking" className="hover:text-white">Airport Parking</Link> · <span className="text-white">{airport?.name || iata}</span></nav>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mt-3 leading-tight" data-testid="landing-h1">{airport?.name || iata} <span className="text-[var(--fpn-amber)]">parking</span></h1>
          <p className="text-slate-200 mt-4 max-w-2xl">{copy.intro}</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 -mt-8">
        <SearchWidget initial={{ airport: iata }} />
      </section>

      <section className="max-w-6xl mx-auto px-5 lg:px-8 mt-16">
        <h2 className="font-display text-2xl md:text-3xl font-semibold text-[var(--fpn-navy)]">Sample parking at {airport?.name || iata}</h2>
        <div className="grid md:grid-cols-2 gap-4 mt-6">
          {offers.map(o => (
            <div key={o.offer_id} className="border border-slate-200 rounded-2xl p-5 bg-white card-lift flex items-center gap-4" data-testid={`landing-offer-${o.offer_id}`}>
              <img src={o.image_url} alt="" className="w-24 h-24 object-cover rounded-xl"/>
              <div className="flex-1">
                <div className="font-semibold text-[var(--fpn-navy)]">{o.name}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><MapPin size={12}/> {o.distance_miles} mi</span>
                  <span>{o.category.replace("_", " ")}</span>
                </div>
                <div className="font-display text-xl font-bold text-[var(--fpn-navy)] mt-2">from £{o.total_price.toFixed(2)}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {copy.tips.length > 0 && (
        <section className="max-w-6xl mx-auto px-5 lg:px-8 mt-16">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-[var(--fpn-navy)]">Tips for parking at {airport?.name || iata}</h2>
          <ul className="mt-6 space-y-3">
            {copy.tips.map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-slate-700"><Check className="text-[var(--fpn-amber-2)] mt-0.5" size={16}/> {t}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-5 lg:px-8 mt-16 mb-16">
        <div className="rounded-2xl bg-slate-50 border border-slate-200 p-8">
          <h3 className="font-display text-xl font-semibold text-[var(--fpn-navy)]">Why Parkpot for {airport?.name || iata}?</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">Parkpot brings every way to park at {airport?.name || iata} into one search — official on-terminal parking, off-airport Park &amp; Ride, valet Meet &amp; Greet, hotel bundles and approved community driveways. Every listing is either supplied by a verified commercial partner or reviewed by our team before it appears here.</p>
        </div>
      </section>
    </div>
  );
}
